/**
 * Pipeline Overview Dashboard
 * High-level overview of the Question Generation Pipeline
 * Shows: Sources → AKUs → Racks → Questions with counts and status
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Separator } from '../ui/separator';
import {
  FileText,
  Database,
  Layers,
  HelpCircle,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  TrendingUp,
  Clock,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API = '';

const PipelineOverviewDashboard = ({ examProfileId = null, isActive = true }) => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [examProfiles, setExamProfiles] = useState([]);
  const [selectedExam, setSelectedExam] = useState(examProfileId || '');

  // Sync selectedExam with examProfileId prop
  useEffect(() => {
    if (examProfileId && examProfileId !== selectedExam) {
      setSelectedExam(examProfileId);
    }
  }, [examProfileId]);

  const fetchExamProfiles = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/question-engine/exams`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setExamProfiles(data);
        if (!selectedExam && data.length > 0) {
          setSelectedExam(data[0].exam_id);
        }
      }
    } catch (error) {
      console.error('Error fetching exam profiles:', error);
    }
  }, [getAuthHeaders, selectedExam]);

  const fetchPipelineStats = useCallback(async () => {
    if (!selectedExam) return;
    
    setLoading(true);
    try {
      // Fetch multiple endpoints in parallel
      const [sourcesRes, akusRes, racksRes, questionsRes] = await Promise.all([
        fetch(`${API}/api/sources?exam_id=${selectedExam}`, { headers: getAuthHeaders() }),
        fetch(`${API}/api/akus/v2?exam_profile_id=${selectedExam}&limit=1`, { headers: getAuthHeaders() }),
        fetch(`${API}/api/racks?exam_profile_id=${selectedExam}&limit=1`, { headers: getAuthHeaders() }),
        fetch(`${API}/api/admin/questions/unified?exam_profile_id=${selectedExam}&limit=1`, { headers: getAuthHeaders() })
      ]);

      const [sourcesData, akusData, racksData, questionsData] = await Promise.all([
        sourcesRes.ok ? sourcesRes.json() : { total: 0 },
        akusRes.ok ? akusRes.json() : { total: 0 },
        racksRes.ok ? racksRes.json() : { total: 0 },
        questionsRes.ok ? questionsRes.json() : { total: 0 }
      ]);

      // Extract counts
      const sourceCount = sourcesData.total || sourcesData.length || 0;
      const akuCount = akusData.total || 0;
      const rackCount = racksData.total || 0;
      const questionCount = questionsData.total || 0;

      // Calculate status distribution for questions
      const statusCounts = {
        DRAFT: 0,
        REVIEW: 0,
        APPROVED: 0,
        PUBLISHED: 0
      };

      if (questionsData.items) {
        questionsData.items.forEach(item => {
          const status = item.status || 'DRAFT';
          if (statusCounts[status] !== undefined) {
            statusCounts[status]++;
          }
        });
      }

      setStats({
        sources: sourceCount,
        akus: akuCount,
        racks: rackCount,
        questions: questionCount,
        questionStatus: statusCounts
      });
    } catch (error) {
      console.error('Error fetching pipeline stats:', error);
      toast.error('Fehler beim Laden der Pipeline-Statistiken');
    } finally {
      setLoading(false);
    }
  }, [selectedExam, getAuthHeaders]);

  useEffect(() => {
    if (isActive) {
      fetchExamProfiles();
    }
  }, [isActive, fetchExamProfiles]);

  useEffect(() => {
    if (isActive && selectedExam) {
      fetchPipelineStats();
    }
  }, [isActive, selectedExam, fetchPipelineStats]);

  const pipelineStages = [
    {
      name: 'Sources',
      icon: FileText,
      count: stats?.sources || 0,
      color: 'bg-blue-500',
      description: 'Quelldokumente'
    },
    {
      name: 'AKUs',
      icon: Database,
      count: stats?.akus || 0,
      color: 'bg-purple-500',
      description: 'Atomic Knowledge Units'
    },
    {
      name: 'Racks',
      icon: Layers,
      count: stats?.racks || 0,
      color: 'bg-orange-500',
      description: 'Fragen-Vorlagen'
    },
    {
      name: 'Questions',
      icon: HelpCircle,
      count: stats?.questions || 0,
      color: 'bg-green-500',
      description: 'Generierte Fragen'
    }
  ];

  const getConversionRate = (from, to) => {
    if (!from || from === 0) return 0;
    return Math.round((to / from) * 100);
  };

  return (
    <Card className="w-full" data-testid="pipeline-overview-dashboard">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Pipeline-Übersicht
            </CardTitle>
            <CardDescription>
              Gesamtstatus der Fragen-Generierung
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={selectedExam}
              onChange={(e) => setSelectedExam(e.target.value)}
              className="px-3 py-1.5 border rounded-md text-sm bg-white dark:bg-slate-800"
              data-testid="exam-selector"
            >
              <option value="">-- Prüfung wählen --</option>
              {examProfiles.map(exam => (
                <option key={exam.exam_id} value={exam.exam_id}>
                  {exam.exam_id} - {exam.name}
                </option>
              ))}
            </select>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchPipelineStats}
              disabled={loading || !selectedExam}
              data-testid="refresh-stats-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {!selectedExam ? (
          <div className="text-center py-8 text-slate-500">
            Bitte wählen Sie eine Prüfung aus, um die Pipeline-Statistiken anzuzeigen.
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Pipeline Flow */}
            <div className="flex items-center justify-between gap-2 overflow-x-auto pb-2">
              {pipelineStages.map((stage, index) => (
                <React.Fragment key={stage.name}>
                  <div className="flex flex-col items-center min-w-[100px]">
                    <div className={`w-12 h-12 rounded-full ${stage.color} flex items-center justify-center mb-2`}>
                      <stage.icon className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-2xl font-bold">{stage.count}</span>
                    <span className="text-xs text-slate-500 text-center">{stage.description}</span>
                  </div>
                  {index < pipelineStages.length - 1 && (
                    <div className="flex flex-col items-center mx-2">
                      <ArrowRight className="w-6 h-6 text-slate-300" />
                      <span className="text-xs text-slate-400">
                        {getConversionRate(pipelineStages[index].count, pipelineStages[index + 1].count)}%
                      </span>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            <Separator />

            {/* Question Status Distribution */}
            {stats?.questions > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Fragen-Status Verteilung
                </h4>
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { status: 'DRAFT', label: 'Entwurf', color: 'bg-slate-200 text-slate-700' },
                    { status: 'REVIEW', label: 'Prüfung', color: 'bg-yellow-100 text-yellow-700' },
                    { status: 'APPROVED', label: 'Genehmigt', color: 'bg-blue-100 text-blue-700' },
                    { status: 'PUBLISHED', label: 'Veröffentlicht', color: 'bg-green-100 text-green-700' }
                  ].map(item => (
                    <div 
                      key={item.status}
                      className={`p-3 rounded-lg ${item.color} text-center`}
                      data-testid={`status-${item.status.toLowerCase()}`}
                    >
                      <div className="text-xl font-bold">
                        {stats.questionStatus?.[item.status] || 0}
                      </div>
                      <div className="text-xs">{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="flex gap-2 pt-2">
              <Badge variant="outline" className="text-xs">
                <Clock className="w-3 h-3 mr-1" />
                Letzte Aktualisierung: {new Date().toLocaleTimeString('de-DE')}
              </Badge>
              {stats?.questions > 0 && stats?.questionStatus?.DRAFT > 0 && (
                <Badge variant="outline" className="text-xs bg-yellow-50">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  {stats.questionStatus.DRAFT} Entwürfe zur Prüfung
                </Badge>
              )}
              {stats?.questions > 0 && stats?.questionStatus?.PUBLISHED > 0 && (
                <Badge variant="outline" className="text-xs bg-green-50">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  {stats.questionStatus.PUBLISHED} veröffentlicht
                </Badge>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PipelineOverviewDashboard;
